import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMonthlyPerformance } from '../overviewV9/hooks/useMonthlyPerformance';
import { useFunnel } from '../overviewV9/hooks/useFunnel';
import { fetchWithBrand } from '../overviewV9/utils/api';
import {
  formatNumberCompact,
} from '../overviewV9/utils/formatters';

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmtCur(v) {
  if (v == null || isNaN(v)) return '₺0';
  const num = Number(v);
  if (num >= 1_000_000) return '₺' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 10_000) return '₺' + Math.round(num / 1_000) + 'K';
  return '₺' + num.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
}

function fmtNum(v) {
  if (v == null || isNaN(v)) return '0';
  return formatNumberCompact(v);
}

function fmtPct(v) {
  if (v == null || isNaN(v)) return '0%';
  return `%${Number(v).toFixed(1)}`;
}

function fmtRoas(v) {
  if (v == null || isNaN(v)) return '0.00';
  return Number(v).toFixed(2);
}

function roasBg(v) {
  const n = Number(v) || 0;
  if (n >= 5) return 'bg-emerald-50 text-emerald-700';
  if (n >= 3) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-600';
}

/** Sort months by displayMonthKey (YYYY-MM) desc, take most recent 6 */
function recentMonths(months) {
  if (!months || !months.length) return [];
  return [...months]
    .sort((a, b) => (b.displayMonthKey || '').localeCompare(a.displayMonthKey || ''))
    .slice(0, 6)
    .reverse();
}

const TH = 'text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider';
const THR = 'text-right py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider';
const THC = 'text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider';
const TD = 'py-2.5 px-4 text-sm text-gray-700';
const TDR = 'py-2.5 px-4 text-sm text-gray-700 text-right';
const TDC = 'py-2.5 px-4 text-sm text-gray-700 text-center';
const TDB = 'py-2.5 px-4 text-sm font-semibold text-gray-900';
const TDBR = 'py-2.5 px-4 text-sm font-semibold text-gray-900 text-right';

// ─── Section wrapper ────────────────────────────────────────────────────────────

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );
}

// ─── Blog Content Plan Data & Component ──────────────────────────────────────────

const BLOG_CATEGORIES = [
  {
    code: 'A', title: 'Genel Bilgi & Tanım',
    items: [
      ['Yenilenmiş iPhone Nedir? İkinci Elden Farkı Ne?', 'Yüksek', 'yenilenmiş iphone nedir'],
      ['Yenilenmiş Telefon Ne Demek? Refurbished Cihaz Rehberi', 'Yüksek', 'yenilenmiş telefon ne demek'],
      ['Yenilenmiş ve Sıfır Telefon Arasındaki Fark Nedir?', 'Yüksek', 'yenilenmiş vs sıfır telefon'],
      ['Yenilenmiş Cihaz Alırken Dikkat Edilmesi Gereken 5 Şey', 'Yüksek', 'yenilenmiş cihaz dikkat'],
      ['Yenilenmiş Telefon Güvenilir mi? Gerçekler ve Mitler', 'Yüksek', 'yenilenmiş telefon güvenilir mi'],
      ['Refurbished Ne Demek? Apple Refurbished vs Bağımsız Yenileme', 'Yüksek', 'refurbished ne demek'],
      ['Yenilenmiş Telefonların Ömrü Ne Kadar?', 'Orta', 'yenilenmiş telefon ömrü'],
      ['Yenilenmiş Telefon ile İkinci El Telefon Aynı Şey mi?', 'Yüksek', 'yenilenmiş vs ikinci el farkı'],
      ['Yenilenmiş Telefon Nasıl Üretilir? Adım Adım Yenileme Süreci', 'Yüksek', 'yenilenmiş telefon üretim süreci'],
      ['Yenilenmiş Telefon Kimlere Uygun? Kimler Tercih Etmeli?', 'Orta', 'yenilenmiş telefon kime uygun'],
      ['Yenilenmiş Telefon Alacaklara 10 Sık Sorulan Soru (SSS)', 'Yüksek', 'yenilenmiş telefon SSS'],
      ['Yenilenmiş Telefonda Orijinal Parça Kullanılıyor mu?', 'Yüksek', 'yenilenmiş telefon orijinal parça'],
      ['Yenilenmiş Telefonlar Neden Bu Kadar Popüler Hale Geldi?', 'Orta', 'yenilenmiş telefon popülerlik'],
      ['Yenilenmiş Telefon Almak Riskli mi? Avantaj ve Dezavantajları', 'Yüksek', 'yenilenmiş telefon avantaj dezavantaj'],
    ],
  },
  {
    code: 'B', title: 'Model Bazlı İnceleme & Satın Alma Rehberleri',
    items: [
      ['Yenilenmiş iPhone 11 Almaya Değer mi? 2026 İncelemesi', 'Yüksek', 'yenilenmiş iphone 11'],
      ['Yenilenmiş iPhone 12 İnceleme: Fiyat, Performans, Değer mi?', 'Yüksek', 'yenilenmiş iphone 12'],
      ['Yenilenmiş iPhone 13 Alınır mı? Detaylı Rehber', 'Yüksek', 'yenilenmiş iphone 13'],
      ['Yenilenmiş iPhone 13 Pro / Pro Max: Farkları ve Avantajları', 'Yüksek', 'yenilenmiş iphone 13 pro'],
      ['Yenilenmiş iPhone 14 İnceleme: Sıfırına Göre Ne Kadar Avantajlı?', 'Yüksek', 'yenilenmiş iphone 14'],
      ['Yenilenmiş iPhone 14 Pro / Pro Max: Dynamic Island Deneyimi', 'Yüksek', 'yenilenmiş iphone 14 pro'],
      ['Yenilenmiş iPhone 15 Almak İçin Doğru Zaman mı?', 'Yüksek', 'yenilenmiş iphone 15'],
      ['Yenilenmiş iPhone 15 Pro / Pro Max: Titanyum Kasa Deneyimi', 'Yüksek', 'yenilenmiş iphone 15 pro'],
      ['Yenilenmiş iPhone SE (2022) İnceleme: Bütçe Dostu Apple Deneyimi', 'Orta', 'yenilenmiş iphone se'],
      ['Yenilenmiş iPhone 13 vs iPhone 14: Hangisini Almalı?', 'Yüksek', 'yenilenmiş iphone 13 vs 14'],
      ['Yenilenmiş iPhone 14 vs iPhone 15: Fark Buna Değer mi?', 'Yüksek', 'yenilenmiş iphone 14 vs 15'],
      ['Yenilenmiş MacBook Air vs Pro: Hangisi Size Uygun?', 'Yüksek', 'yenilenmiş macbook air vs pro'],
      ['Yenilenmiş MacBook Air M1 Alınır mı? 2026 Değerlendirmesi', 'Yüksek', 'yenilenmiş macbook air m1'],
      ['Yenilenmiş MacBook Pro M2 / M3 İnceleme', 'Orta', 'yenilenmiş macbook pro m2'],
      ['Bütçeye Göre En İyi Yenilenmiş iPhone Modeli Hangisi?', 'Yüksek', 'bütçeye göre yenilenmiş iphone'],
      ['2026\'da En İyi Yenilenmiş Telefonlar: Top 10 Liste', 'Yüksek', 'en iyi yenilenmiş telefon 2026'],
    ],
  },
  {
    code: 'C', title: 'Teknik & Kalite Kontrol',
    items: [
      ['Yenilenmiş Telefonda Batarya Sağlığı Nasıl Kontrol Edilir?', 'Yüksek', 'yenilenmiş telefon batarya sağlığı'],
      ['Yenilenmiş iPhone\'da Ekran Orijinal mi? Nasıl Anlaşılır?', 'Yüksek', 'yenilenmiş iphone orijinal ekran'],
      ['Grade A, B, C Ne Demek? Yenilenmiş Cihaz Kalite Sınıfları', 'Yüksek', 'yenilenmiş cihaz grade'],
      ['Yenilenmiş Telefonda Su Geçirmezlik Korunuyor mu?', 'Orta', 'yenilenmiş telefon su geçirmezlik'],
      ['IMEI Sorgulama ile Yenilenmiş Cihaz Geçmişi Nasıl Öğrenilir?', 'Orta', 'yenilenmiş telefon IMEI sorgulama'],
      ['Yenilenmiş Cihazlarda Hangi Parçalar Değiştirilir?', 'Orta', 'yenilenmiş cihaz değişen parçalar'],
      ['Yenilenmiş Telefonda Kamera Kalitesi Düşer mi?', 'Düşük', 'yenilenmiş telefon kamera kalitesi'],
    ],
  },
  {
    code: 'D', title: 'Yasal & Garanti & Tüketici Hakları',
    items: [
      ['Türkiye\'de Yenilenmiş Telefon Yönetmelikleri ve Tüketici Hakları', 'Yüksek', 'yenilenmiş telefon yönetmelik türkiye'],
      ['Yenilenmiş Cihazlarda Garanti Süresi Ne Kadar?', 'Yüksek', 'yenilenmiş cihaz garanti süresi'],
      ['Yenilenmiş Telefon İade ve Değişim Koşulları Nelerdir?', 'Yüksek', 'yenilenmiş telefon iade koşulları'],
      ['BTK Kayıt ve Yenilenmiş Telefon: Bilmeniz Gerekenler', 'Orta', 'yenilenmiş telefon BTK kayıt'],
      ['Yenilenmiş Cihaz Faturası Neden Önemli?', 'Orta', 'yenilenmiş cihaz fatura önemi'],
      ['Yenilenmiş Telefon Alırken Garanti Belgesi Nasıl Olmalı?', 'Orta', 'yenilenmiş telefon garanti belgesi'],
      ['Yenilenmiş Cihaz Satıcısı Nasıl Seçilir? Güvenilir Satıcı Kriterleri', 'Yüksek', 'güvenilir yenilenmiş telefon satıcısı'],
    ],
  },
  {
    code: 'E', title: 'Bakım, Kullanım & Uzun Ömür',
    items: [
      ['Yenilenmiş iPhone Bataryası Nasıl Uzun Ömürlü Kullanılır?', 'Orta', 'yenilenmiş iphone batarya ömrü'],
      ['Yenilenmiş Telefonu İlk Aldığınızda Yapmanız Gereken 7 Şey', 'Yüksek', 'yenilenmiş telefon ilk kurulum'],
      ['Yenilenmiş Cihazda iOS Güncelleme Alır mı?', 'Yüksek', 'yenilenmiş iphone güncelleme'],
      ['Yenilenmiş Telefonun Değerini Korumak İçin 5 İpucu', 'Düşük', 'yenilenmiş telefon değer koruma'],
      ['Yenilenmiş Telefona Kılıf ve Ekran Koruyucu Şart mı?', 'Düşük', 'yenilenmiş telefon aksesuar'],
      ['Yenilenmiş Cihazda iCloud/Google Hesap Kilidi Sorunu ve Çözümü', 'Orta', 'yenilenmiş telefon hesap kilidi'],
      ['Yenilenmiş MacBook Batarya Cycle Sayısı Neden Önemli?', 'Orta', 'yenilenmiş macbook batarya cycle'],
    ],
  },
  {
    code: 'F', title: 'Fiyat, Taksit & Ekonomi',
    items: [
      ['Yenilenmiş iPhone Fiyatları 2026: Sıfıra Göre Ne Kadar Tasarruf?', 'Yüksek', 'yenilenmiş iphone fiyat 2026'],
      ['Yenilenmiş Telefon Taksitle Alınır mı? Ödeme Seçenekleri', 'Yüksek', 'yenilenmiş telefon taksit'],
      ['Neden Sıfır Yerine Yenilenmiş Tercih Etmelisiniz? Maliyet Analizi', 'Yüksek', 'yenilenmiş telefon tasarruf'],
      ['Yenilenmiş Cihaz Fiyatları Neden Farklılık Gösteriyor?', 'Orta', 'yenilenmiş cihaz fiyat farkı'],
      ['Öğrenciler İçin En Uygun Yenilenmiş Telefon Önerileri', 'Orta', 'öğrenci yenilenmiş telefon'],
      ['Yenilenmiş Telefon Alarak Yılda Ne Kadar Tasarruf Edersiniz?', 'Orta', 'yenilenmiş telefon yıllık tasarruf'],
      ['Yenilenmiş Cihaz Piyasası Türkiye\'de Nasıl Büyüyor?', 'Düşük', 'yenilenmiş cihaz piyasası türkiye'],
    ],
  },
  {
    code: 'G', title: 'Sürdürülebilirlik & Çevre',
    items: [
      ['Yenilenmiş Telefon Almak Çevreye Nasıl Katkı Sağlar?', 'Yüksek', 'yenilenmiş telefon çevre katkı'],
      ['E-Atık Nedir? Yenilenmiş Cihaz Tercihinin Ekolojik Etkisi', 'Orta', 'e-atık yenilenmiş cihaz'],
      ['Döngüsel Ekonomi ve Yenilenmiş Teknoloji: Geleceğin Tüketim Modeli', 'Orta', 'döngüsel ekonomi yenilenmiş teknoloji'],
      ['Bir Yenilenmiş iPhone ile Ne Kadar Karbon Ayak İzi Azaltılır?', 'Düşük', 'yenilenmiş iphone karbon ayak izi'],
      ['Sürdürülebilir Teknoloji Tüketimi: Neden Yenilenmiş Tercih Etmeli?', 'Orta', 'sürdürülebilir teknoloji tüketimi'],
      ['Yenilenmiş Cihaz Sektörü Dünyada ve Türkiye\'de Nereye Gidiyor?', 'Düşük', 'yenilenmiş cihaz sektörü trend'],
    ],
  },
];

const BLOG_TOTAL = BLOG_CATEGORIES.reduce((s, c) => s + c.items.length, 0);
const BLOG_HIGH = BLOG_CATEGORIES.reduce((s, c) => s + c.items.filter(i => i[1] === 'Yüksek').length, 0);
const BLOG_MED = BLOG_CATEGORIES.reduce((s, c) => s + c.items.filter(i => i[1] === 'Orta').length, 0);
const BLOG_LOW = BLOG_TOTAL - BLOG_HIGH - BLOG_MED;

function priorityBadge(p) {
  const cls = p === 'Yüksek' ? 'bg-gray-900 text-white' : p === 'Orta' ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-500';
  return <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${cls}`}>{p}</span>;
}

function BlogContentPlan() {
  let globalIndex = 0;

  return (
    <Section title="11. Blog İçerik Planı — Yenilenmiş Cihaz SEO Stratejisi" subtitle={`${BLOG_TOTAL} blog yazısı — Organik trafiği artırmak için soru bazlı içerik haritası`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <SummaryCard label="Toplam İçerik" value={`${BLOG_TOTAL} Yazı`} sub="Soru bazlı blog içerikleri" />
        <SummaryCard label="Hedef" value="Organik Trafik" sub="SEO & bilgi amaçlı arama trafiği" />
        <SummaryCard label="Kategori" value={`${BLOG_CATEGORIES.length} Ana Başlık`} sub="Genel bilgi, model inceleme, teknik, yasal, bakım, fiyat, çevre" />
      </div>

      {BLOG_CATEGORIES.map((cat) => {
        const startIdx = globalIndex;
        globalIndex += cat.items.length;
        return (
          <div key={cat.code} className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex px-2.5 py-1 text-[10px] font-bold rounded bg-gray-900 text-white">{cat.code}</span>
              <h3 className="text-sm font-bold text-gray-900">{cat.title}</h3>
              <span className="text-[10px] text-gray-400 ml-auto">{cat.items.length} içerik</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className={TH} style={{ width: 40 }}>#</th>
                    <th className={TH}>Blog Başlığı / Soru</th>
                    <th className={THC} style={{ width: 100 }}>Öncelik</th>
                    <th className={TH} style={{ width: 200 }}>Hedef Anahtar Kelime</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cat.items.map(([title, priority, keyword], i) => (
                    <tr key={i}>
                      <td className={TD}>{startIdx + i + 1}</td>
                      <td className={TDB}>{title}</td>
                      <td className={TDC}>{priorityBadge(priority)}</td>
                      <td className={`${TD} text-gray-500`}>{keyword}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Özet dağılım */}
      <div className="mt-8 pt-6 border-t">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Öncelik Dağılımı</p>
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            {priorityBadge('Yüksek')}
            <span className="text-gray-700 font-semibold">{BLOG_HIGH} içerik</span>
          </div>
          <div className="flex items-center gap-2">
            {priorityBadge('Orta')}
            <span className="text-gray-700 font-semibold">{BLOG_MED} içerik</span>
          </div>
          <div className="flex items-center gap-2">
            {priorityBadge('Düşük')}
            <span className="text-gray-700 font-semibold">{BLOG_LOW} içerik</span>
          </div>
        </div>
        <div className="h-4 flex rounded overflow-hidden mt-3">
          <div className="bg-gray-900 h-full" style={{ width: `${(BLOG_HIGH / BLOG_TOTAL * 100).toFixed(0)}%` }} />
          <div className="bg-gray-300 h-full" style={{ width: `${(BLOG_MED / BLOG_TOTAL * 100).toFixed(0)}%` }} />
          <div className="bg-gray-100 h-full" style={{ width: `${(BLOG_LOW / BLOG_TOTAL * 100).toFixed(0)}%` }} />
        </div>
      </div>
    </Section>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────────

export default function MarketingReports() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);

  // Core hooks
  const monthly = useMonthlyPerformance(brandId, 'all', null, null);
  const funnel = useFunnel(brandId, 'last30', null, null);

  // Additional API data
  const [campaignData, setCampaignData] = useState(null);
  const [keywordData, setKeywordData] = useState(null);
  const [metaCreativeData, setMetaCreativeData] = useState(null);
  const [metaCreativeDetails, setMetaCreativeDetails] = useState(null);
  const [itemPerformance, setItemPerformance] = useState(null);
  const [extraLoading, setExtraLoading] = useState(true);

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;

    async function fetchAll() {
      setExtraLoading(true);
      try {
        const results = await Promise.allSettled([
          fetchWithBrand('/api/google-ads/combined-campaign-performance', brandId).then(r => r.ok ? r.json() : null),
          fetchWithBrand('/api/google-ads/keyword-full-performance?dateRange=last180', brandId).then(r => r.ok ? r.json() : null),
          fetchWithBrand('/api/google-ads/meta-creative-performance-raw?dateRange=last180', brandId).then(r => r.ok ? r.json() : null),
          fetchWithBrand('/api/google-ads/meta-creative-details', brandId).then(r => r.ok ? r.json() : null),
          fetchWithBrand('/api/google-ads/monthly-item-performance?dateRange=all', brandId).then(r => r.ok ? r.json() : null),
        ]);
        if (cancelled) return;
        setCampaignData(results[0].status === 'fulfilled' ? results[0].value : null);
        setKeywordData(results[1].status === 'fulfilled' ? results[1].value : null);
        setMetaCreativeData(results[2].status === 'fulfilled' ? results[2].value : null);
        setMetaCreativeDetails(results[3].status === 'fulfilled' ? results[3].value : null);
        setItemPerformance(results[4].status === 'fulfilled' ? results[4].value : null);
      } catch (e) {
        console.error('MarketingReports fetch error:', e);
      } finally {
        if (!cancelled) setExtraLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [brandId]);

  // ── Derived data ──────────────────────────────────────────────────────────────

  const perfData = monthly.monthlyPerformanceData;
  const catData = monthly.monthlyPlatformCategoryData;

  // Last 6 months for Google & Meta
  const googleMonths = useMemo(() => recentMonths(perfData?.['Google Ads']?.months || []), [perfData]);
  const metaMonths = useMemo(() => recentMonths(perfData?.['Meta Ads']?.months || []), [perfData]);

  // Merge months by displayMonthKey (YYYY-MM) for reliable matching
  const mergedMonths = useMemo(() => {
    const map = {};
    googleMonths.forEach(m => {
      const key = m.displayMonthKey || m.month;
      map[key] = { key, month: m.month, gCost: m.cost || 0, gRevenue: m.ga4_total_revenue || 0, gRoas: m.ga4_roas || 0 };
    });
    metaMonths.forEach(m => {
      const key = m.displayMonthKey || m.month;
      if (!map[key]) map[key] = { key, month: m.month, gCost: 0, gRevenue: 0, gRoas: 0 };
      map[key].mCost = m.cost || 0;
      map[key].mRevenue = m.ga4_total_revenue || 0;
      map[key].mRoas = m.ga4_roas || 0;
    });
    return Object.values(map)
      .sort((a, b) => (a.key || '').localeCompare(b.key || ''))
      .map(r => ({
        ...r,
        mCost: r.mCost || 0,
        mRevenue: r.mRevenue || 0,
        mRoas: r.mRoas || 0,
        totalCost: (r.gCost || 0) + (r.mCost || 0),
        totalRevenue: (r.gRevenue || 0) + (r.mRevenue || 0),
      })).map(r => ({
        ...r,
        totalRoas: r.totalCost > 0 ? r.totalRevenue / r.totalCost : 0,
      }));
  }, [googleMonths, metaMonths]);

  // Campaign filter state
  const [campaignFilter, setCampaignFilter] = useState('all');

  // Unified campaigns from monthly hook + API campaign data
  const topCampaigns = useMemo(() => {
    let campaigns = [];

    // From unified campaign data (monthly hook)
    if (monthly.unifiedCampaignData) {
      campaigns = monthly.unifiedCampaignData.map(c => ({
        name: c.campaign_name || c.campaignName || c.name || 'Unknown',
        platform: c.platform || (c.source === 'meta' ? 'Meta' : 'Google'),
        cost: c.cost || c.spend || 0,
        clicks: c.clicks || 0,
        conversions: c.ga4_purchases || c.conversions || 0,
        revenue: c.ga4_total_revenue || c.revenue || 0,
        roas: c.ga4_roas || (c.cost > 0 ? (c.ga4_total_revenue || 0) / c.cost : 0),
      }));
    }

    // Also merge combined-campaign-performance API data if available
    if (campaignData && Array.isArray(campaignData)) {
      const existing = new Set(campaigns.map(c => c.name));
      campaignData.forEach(c => {
        const name = c.campaign_name || c.campaignName || c.name || 'Unknown';
        if (!existing.has(name)) {
          campaigns.push({
            name,
            platform: c.platform || (c.source === 'meta' ? 'Meta' : 'Google'),
            cost: c.cost || c.spend || 0,
            clicks: c.clicks || 0,
            conversions: c.ga4_purchases || c.conversions || 0,
            revenue: c.ga4_total_revenue || c.revenue || 0,
            roas: c.ga4_roas || (c.cost > 0 ? (c.ga4_total_revenue || 0) / c.cost : 0),
          });
        }
      });
    }

    // Filter
    if (campaignFilter === 'google') campaigns = campaigns.filter(c => /google/i.test(c.platform));
    if (campaignFilter === 'meta') campaigns = campaigns.filter(c => /meta/i.test(c.platform));

    return campaigns
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 20);
  }, [monthly.unifiedCampaignData, campaignData, campaignFilter]);

  // Keywords — aggregate duplicates by keyword text
  const topKeywords = useMemo(() => {
    if (!keywordData) return [];
    const rows = Array.isArray(keywordData) ? keywordData : keywordData?.data || keywordData?.keywords || [];
    const map = {};
    rows.forEach(k => {
      const text = (k.keyword || k.keyword_text || k.search_term || '').toLowerCase().trim();
      if (!text) return;
      if (!map[text]) {
        map[text] = { keyword: k.keyword || k.keyword_text || k.search_term, clicks: 0, cost: 0, conversions: 0, revenue: 0 };
      }
      map[text].clicks += Number(k.clicks) || 0;
      map[text].cost += Number(k.cost) || (k.cost_micros ? k.cost_micros / 1e6 : 0);
      map[text].conversions += Number(k.conversions) || 0;
      map[text].revenue += Number(k.conversion_value || k.revenue) || 0;
    });
    return Object.values(map)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 30);
  }, [keywordData]);

  // Meta creatives merged with details
  const topCreatives = useMemo(() => {
    if (!metaCreativeData) return [];
    const raw = Array.isArray(metaCreativeData) ? metaCreativeData : metaCreativeData?.data || [];
    const detailMap = {};
    if (metaCreativeDetails) {
      const details = Array.isArray(metaCreativeDetails) ? metaCreativeDetails : metaCreativeDetails?.data || [];
      details.forEach(d => {
        const key = d.ad_id || d.adId || d.creative_id;
        if (key) detailMap[key] = d;
      });
    }
    return raw
      .map(c => {
        const id = c.ad_id || c.adId || c.creative_id;
        const detail = detailMap[id] || {};
        return {
          id,
          name: c.ad_name || c.adName || c.name || detail.ad_name || 'Unknown',
          thumbnail: detail.thumbnail_url || detail.image_url || detail.thumbnailUrl || c.thumbnail_url || c.image_url || null,
          spend: c.spend || c.cost || 0,
          clicks: c.clicks || 0,
          purchases: c.purchases || c.conversions || c.ga4_purchases || 0,
          revenue: c.revenue || c.ga4_total_revenue || c.purchase_value || 0,
          roas: c.roas || (c.spend > 0 ? (c.revenue || c.ga4_total_revenue || 0) / c.spend : 0),
        };
      })
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 15);
  }, [metaCreativeData, metaCreativeDetails]);

  // Remarketing vs Prospecting
  const remarketingAnalysis = useMemo(() => {
    const allCampaigns = monthly.unifiedCampaignData || [];
    const rmPattern = /retarget|remarketing|rtg|dpa|dynamic/i;
    const rmCampaigns = allCampaigns.filter(c => rmPattern.test(c.campaign_name || c.campaignName || c.name || ''));
    const prospCampaigns = allCampaigns.filter(c => !rmPattern.test(c.campaign_name || c.campaignName || c.name || ''));

    const sum = (arr) => ({
      cost: arr.reduce((s, c) => s + (c.cost || c.spend || 0), 0),
      revenue: arr.reduce((s, c) => s + (c.ga4_total_revenue || c.revenue || 0), 0),
      clicks: arr.reduce((s, c) => s + (c.clicks || 0), 0),
      conversions: arr.reduce((s, c) => s + (c.ga4_purchases || c.conversions || 0), 0),
    });

    const rm = sum(rmCampaigns);
    const pr = sum(prospCampaigns);
    rm.roas = rm.cost > 0 ? rm.revenue / rm.cost : 0;
    pr.roas = pr.cost > 0 ? pr.revenue / pr.cost : 0;
    const totalCost = rm.cost + pr.cost;
    const totalRevenue = rm.revenue + pr.revenue;
    rm.costShare = totalCost > 0 ? (rm.cost / totalCost * 100) : 0;
    pr.costShare = totalCost > 0 ? (pr.cost / totalCost * 100) : 0;
    rm.revenueShare = totalRevenue > 0 ? (rm.revenue / totalRevenue * 100) : 0;
    pr.revenueShare = totalRevenue > 0 ? (pr.revenue / totalRevenue * 100) : 0;
    rm.count = rmCampaigns.length;
    pr.count = prospCampaigns.length;

    return { remarketing: rm, prospecting: pr };
  }, [monthly.unifiedCampaignData]);

  // Item performance — backend returns monthly rows with totalRevenue/totalItemsPurchased;
  // aggregate by itemId to get per-product totals
  const topItems = useMemo(() => {
    if (!itemPerformance) return [];
    const rows = Array.isArray(itemPerformance) ? itemPerformance : itemPerformance?.data || itemPerformance?.items || [];
    if (rows.length === 0) return [];

    const map = {};
    for (const r of rows) {
      const id = r.itemId || r.item_id || r.itemName || r.item_name || 'unknown';
      if (!map[id]) {
        map[id] = {
          item_name: r.itemName || r.item_name || r.mcTitle || r.product_name || r.name || '—',
          revenue: 0,
          quantity: 0,
        };
      }
      map[id].revenue += Number(r.totalRevenue || r.total_revenue || r.revenue || r.item_revenue || 0);
      map[id].quantity += Number(r.totalItemsPurchased || r.total_items_purchased || r.quantity || r.units_sold || r.items_sold || 0);
    }

    return Object.values(map)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 20);
  }, [itemPerformance]);

  // Budget distribution (Performance vs Branding)
  const budgetDistribution = useMemo(() => {
    if (!catData) return [];
    const gPerf = catData['Google Ads']?.Performance?.months || [];
    const gBrand = catData['Google Ads']?.Branding?.months || [];
    const mPerf = catData['Meta Ads']?.Performance?.months || [];
    const mBrand = catData['Meta Ads']?.Branding?.months || [];

    const monthMap = {};
    const addToMap = (months, type) => {
      months.forEach(m => {
        const key = m.displayMonthKey || m.month;
        if (!monthMap[key]) monthMap[key] = { key, month: m.month, perfCost: 0, brandCost: 0 };
        if (type === 'perf') monthMap[key].perfCost += m.cost || 0;
        else monthMap[key].brandCost += m.cost || 0;
      });
    };
    addToMap(gPerf, 'perf');
    addToMap(mPerf, 'perf');
    addToMap(gBrand, 'brand');
    addToMap(mBrand, 'brand');

    return Object.values(monthMap)
      .map(r => {
        const total = r.perfCost + r.brandCost;
        r.perfPct = total > 0 ? (r.perfCost / total * 100) : 0;
        r.brandPct = total > 0 ? (r.brandCost / total * 100) : 0;
        r.total = total;
        return r;
      })
      .sort((a, b) => (b.key || '').localeCompare(a.key || ''))
      .slice(0, 6)
      .reverse();
  }, [catData]);

  const isLoading = monthly.isLoading;

  if (!brandId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <p className="text-gray-500">Lütfen bir marka seçin.</p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
      {/* Header */}
      <div className="bg-[#111] p-6 text-white rounded-2xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Marketing Reports</h1>
            <p className="text-neutral-400 text-sm mt-1">
              {selectedBrand?.name || 'Marka'} — Dinamik Performans Raporları
            </p>
          </div>
          <div className="flex gap-2">
            <span className="inline-flex items-center px-3 py-1 bg-white/10 text-xs font-medium rounded-full border border-white/20">
              Son 6 Ay
            </span>
            <span className="inline-flex items-center px-3 py-1 bg-white/10 text-xs font-medium rounded-full border border-white/20">
              Tüm Kanallar
            </span>
          </div>
        </div>
      </div>

      {isLoading ? <LoadingSpinner /> : (
        <>
          {/* ══════════════════ BÖLÜM 1 — Toplam Reklam Harcaması ══════════════════ */}
          <Section title="1. Toplam Reklam Harcaması" subtitle="Son 6 ay — Google Ads, Meta Ads kanal bazlı">
            {mergedMonths.length === 0 ? (
              <p className="text-sm text-gray-400">Veri bulunamadı.</p>
            ) : (
              <>
                {/* Bar chart */}
                <div className="mb-6 space-y-3">
                  {mergedMonths.map((m, i) => {
                    const max = Math.max(...mergedMonths.map(r => r.totalCost), 1);
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-24 shrink-0 truncate">{m.month}</span>
                        <div className="flex-1 flex h-6 rounded overflow-hidden bg-gray-50">
                          <div className="bg-red-400 h-full" style={{ width: `${(m.gCost / max) * 100}%` }} title={`Google: ${fmtCur(m.gCost)}`} />
                          <div className="bg-blue-400 h-full" style={{ width: `${(m.mCost / max) * 100}%` }} title={`Meta: ${fmtCur(m.mCost)}`} />
                        </div>
                        <span className="text-xs font-semibold text-gray-700 w-20 text-right">{fmtCur(m.totalCost)}</span>
                      </div>
                    );
                  })}
                  <div className="flex gap-4 mt-2">
                    <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-3 h-3 rounded bg-red-400" /> Google Ads</span>
                    <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-3 h-3 rounded bg-blue-400" /> Meta Ads</span>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className={TH}>Ay</th>
                        <th className={THR}>Google Ads</th>
                        <th className={THR}>Meta Ads</th>
                        <th className={THR}>Toplam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mergedMonths.map((m, i) => (
                        <tr key={i}>
                          <td className={TDB}>{m.month}</td>
                          <td className={TDR}>{fmtCur(m.gCost)}</td>
                          <td className={TDR}>{fmtCur(m.mCost)}</td>
                          <td className={TDBR}>{fmtCur(m.totalCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-900">
                        <td className={TDB}>Toplam</td>
                        <td className={TDBR}>{fmtCur(mergedMonths.reduce((s, m) => s + m.gCost, 0))}</td>
                        <td className={TDBR}>{fmtCur(mergedMonths.reduce((s, m) => s + m.mCost, 0))}</td>
                        <td className={TDBR}>{fmtCur(mergedMonths.reduce((s, m) => s + m.totalCost, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <SummaryCard label="Toplam Harcama" value={fmtCur(mergedMonths.reduce((s, m) => s + m.totalCost, 0))} />
                  <SummaryCard label="Aylık Ortalama" value={fmtCur(mergedMonths.reduce((s, m) => s + m.totalCost, 0) / (mergedMonths.length || 1))} />
                  <SummaryCard
                    label="En Yüksek Ay"
                    value={fmtCur(Math.max(...mergedMonths.map(m => m.totalCost)))}
                    sub={mergedMonths.reduce((best, m) => m.totalCost > (best?.totalCost || 0) ? m : best, null)?.month}
                  />
                </div>
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 2 — Satış & Ciro ══════════════════ */}
          <Section title="2. Reklamdan Gelen Satış & Ciro" subtitle="Son 6 ay — Kanal bazlı gelir">
            {mergedMonths.length === 0 ? (
              <p className="text-sm text-gray-400">Veri bulunamadı.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className={TH}>Ay</th>
                        <th className={THR}>Google Ads Gelir</th>
                        <th className={THR}>Meta Ads Gelir</th>
                        <th className={THR}>Toplam Gelir</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mergedMonths.map((m, i) => (
                        <tr key={i}>
                          <td className={TDB}>{m.month}</td>
                          <td className={TDR}>{fmtCur(m.gRevenue)}</td>
                          <td className={TDR}>{fmtCur(m.mRevenue)}</td>
                          <td className={TDBR}>{fmtCur(m.totalRevenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-900">
                        <td className={TDB}>Toplam</td>
                        <td className={TDBR}>{fmtCur(mergedMonths.reduce((s, m) => s + m.gRevenue, 0))}</td>
                        <td className={TDBR}>{fmtCur(mergedMonths.reduce((s, m) => s + m.mRevenue, 0))}</td>
                        <td className={TDBR}>{fmtCur(mergedMonths.reduce((s, m) => s + m.totalRevenue, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <SummaryCard label="Toplam Gelir" value={fmtCur(mergedMonths.reduce((s, m) => s + m.totalRevenue, 0))} />
                  <SummaryCard label="Aylık Ortalama" value={fmtCur(mergedMonths.reduce((s, m) => s + m.totalRevenue, 0) / (mergedMonths.length || 1))} />
                  <SummaryCard
                    label="En Yüksek Ay"
                    value={fmtCur(Math.max(...mergedMonths.map(m => m.totalRevenue)))}
                    sub={mergedMonths.reduce((best, m) => m.totalRevenue > (best?.totalRevenue || 0) ? m : best, null)?.month}
                  />
                </div>
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 3 — ROAS Raporu ══════════════════ */}
          <Section title="3. ROAS Raporu" subtitle="Return on Ad Spend — Kanal bazlı aylık">
            {mergedMonths.length === 0 ? (
              <p className="text-sm text-gray-400">Veri bulunamadı.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className={TH}>Ay</th>
                        <th className={THC}>Google ROAS</th>
                        <th className={THC}>Meta ROAS</th>
                        <th className={THC}>Toplam ROAS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mergedMonths.map((m, i) => (
                        <tr key={i}>
                          <td className={TDB}>{m.month}</td>
                          <td className={TDC}>
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(m.gRoas)}`}>
                              {fmtRoas(m.gRoas)}x
                            </span>
                          </td>
                          <td className={TDC}>
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(m.mRoas)}`}>
                              {fmtRoas(m.mRoas)}x
                            </span>
                          </td>
                          <td className={TDC}>
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(m.totalRoas)}`}>
                              {fmtRoas(m.totalRoas)}x
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-4 mt-4 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> ROAS &ge; 5</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> ROAS 3-5</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> ROAS &lt; 3</span>
                </div>
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 4 — En Çok Satış Getiren Kampanyalar ══════════════════ */}
          <Section title="4. En Çok Satış Getiren Kampanyalar" subtitle="Son 6 ay — Gelire göre sıralı Top 20">
            <div className="flex gap-2 mb-4">
              {['all', 'google', 'meta'].map(f => (
                <button
                  key={f}
                  onClick={() => setCampaignFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    campaignFilter === f
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {f === 'all' ? 'Tümü' : f === 'google' ? 'Google Ads' : 'Meta Ads'}
                </button>
              ))}
            </div>

            {topCampaigns.length === 0 ? (
              <p className="text-sm text-gray-400">Kampanya verisi bulunamadı.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-900">
                      <th className={TH}>#</th>
                      <th className={TH}>Kampanya Adı</th>
                      <th className={THC}>Platform</th>
                      <th className={THR}>Harcama</th>
                      <th className={THR}>Tıklama</th>
                      <th className={THR}>Dönüşüm</th>
                      <th className={THR}>Gelir</th>
                      <th className={THC}>ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {topCampaigns.map((c, i) => (
                      <tr key={i}>
                        <td className={TD}>{i + 1}</td>
                        <td className={`${TD} max-w-[250px] truncate`} title={c.name}>{c.name}</td>
                        <td className={TDC}>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded ${
                            /google/i.test(c.platform) ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                          }`}>
                            {/google/i.test(c.platform) ? 'Google' : 'Meta'}
                          </span>
                        </td>
                        <td className={TDR}>{fmtCur(c.cost)}</td>
                        <td className={TDR}>{fmtNum(c.clicks)}</td>
                        <td className={TDR}>{fmtNum(c.conversions)}</td>
                        <td className={TDBR}>{fmtCur(c.revenue)}</td>
                        <td className={TDC}>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(c.roas)}`}>
                            {fmtRoas(c.roas)}x
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 5 — Anahtar Kelime Raporu ══════════════════ */}
          <Section title="5. Anahtar Kelime Raporu" subtitle="Son 180 gün — Dönüşüm değerine göre sıralı Top 30">
            {extraLoading ? <LoadingSpinner /> : topKeywords.length === 0 ? (
              <p className="text-sm text-gray-400">Keyword verisi bulunamadı.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className={TH}>#</th>
                        <th className={TH}>Anahtar Kelime</th>
                        <th className={THR}>Tıklama</th>
                        <th className={THR}>Maliyet</th>
                        <th className={THR}>Dönüşüm</th>
                        <th className={THR}>Gelir</th>
                        <th className={THC}>ROAS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topKeywords.map((k, i) => {
                        const roas = k.cost > 0 ? k.revenue / k.cost : 0;
                        return (
                          <tr key={i}>
                            <td className={TD}>{i + 1}</td>
                            <td className={`${TD} max-w-[250px] truncate`}>{k.keyword || '—'}</td>
                            <td className={TDR}>{fmtNum(k.clicks)}</td>
                            <td className={TDR}>{fmtCur(k.cost)}</td>
                            <td className={TDR}>{fmtNum(k.conversions)}</td>
                            <td className={TDBR}>{fmtCur(k.revenue)}</td>
                            <td className={TDC}>
                              <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(roas)}`}>
                                {fmtRoas(roas)}x
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {topKeywords.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                    <SummaryCard
                      label="En Pahalı Keyword"
                      value={[...topKeywords].sort((a, b) => b.cost - a.cost)[0]?.keyword || '—'}
                      sub={fmtCur([...topKeywords].sort((a, b) => b.cost - a.cost)[0]?.cost)}
                    />
                    <SummaryCard
                      label="En Çok Dönüştüren Keyword"
                      value={[...topKeywords].sort((a, b) => b.conversions - a.conversions)[0]?.keyword || '—'}
                      sub={`${fmtNum([...topKeywords].sort((a, b) => b.conversions - a.conversions)[0]?.conversions)} dönüşüm`}
                    />
                  </div>
                )}
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 6 — Kreatif Performans ══════════════════ */}
          <Section title="6. Kreatif Performans (Meta)" subtitle="Son 180 gün — Harcamaya göre sıralı Top 15">
            {extraLoading ? <LoadingSpinner /> : topCreatives.length === 0 ? (
              <p className="text-sm text-gray-400">Kreatif verisi bulunamadı.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {topCreatives.map((c, i) => (
                  <div key={i} className="rounded-xl border overflow-hidden">
                    {/* Thumbnail */}
                    <div className="bg-gray-100 h-40 flex items-center justify-center overflow-hidden">
                      {c.thumbnail ? (
                        <img src={c.thumbnail} alt={c.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-gray-300 text-xs">Görsel yok</div>
                      )}
                    </div>
                    {/* Metrics */}
                    <div className="p-4 space-y-2">
                      <p className="text-xs font-semibold text-gray-900 truncate" title={c.name}>{c.name}</p>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div>
                          <p className="text-gray-400">Harcama</p>
                          <p className="font-semibold text-gray-900">{fmtCur(c.spend)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Tıklama</p>
                          <p className="font-semibold text-gray-900">{fmtNum(c.clicks)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Satın Alma</p>
                          <p className="font-semibold text-gray-900">{fmtNum(c.purchases)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Gelir</p>
                          <p className="font-semibold text-gray-900">{fmtCur(c.revenue)}</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(c.roas)}`}>
                          ROAS: {fmtRoas(c.roas)}x
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 7 — Funnel Analizi ══════════════════ */}
          <Section title="7. Dönüşüm Funnel Analizi" subtitle="Son 30 gün — 5 adımlı funnel">
            {funnel.isLoading ? <LoadingSpinner /> : (
              <>
                <div className="space-y-3 mb-6">
                  {funnel.funnelData.steps.map((step, i) => {
                    const maxVal = funnel.funnelData.steps[0]?.value || 1;
                    const pct = maxVal > 0 ? (step.value / maxVal * 100) : 0;
                    const prevStep = i > 0 ? funnel.funnelData.steps[i - 1] : null;
                    const dropPct = prevStep && prevStep.value > 0
                      ? ((1 - step.value / prevStep.value) * 100)
                      : 0;

                    return (
                      <div key={i} className="flex items-center gap-4">
                        <span className="text-xs font-medium text-gray-700 w-28 shrink-0">{step.name}</span>
                        <div className="flex-1">
                          <div className="h-8 bg-gray-50 rounded overflow-hidden relative">
                            <div
                              className="h-full bg-gray-900 rounded transition-all"
                              style={{ width: `${Math.max(pct, 2)}%` }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white mix-blend-difference">
                              {fmtNum(step.value)}
                            </span>
                          </div>
                        </div>
                        <span className="text-[10px] text-gray-400 w-16 text-right">
                          {i > 0 ? `−${dropPct.toFixed(1)}%` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <SummaryCard label="Dönüşüm Oranı" value={fmtPct(funnel.funnelData.summary.conversionRate)} />
                  <SummaryCard label="Toplam Gelir" value={fmtCur(funnel.funnelData.summary.revenue)} />
                  <SummaryCard label="Ort. Sepet Tutarı" value={fmtCur(funnel.funnelData.summary.avgOrderValue)} />
                  <SummaryCard label="Kayıp Oranı" value={fmtPct(funnel.funnelData.summary.dropoffRate)} />
                </div>
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 8 — Remarketing vs Prospecting ══════════════════ */}
          <Section title="8. Remarketing / Retargeting Raporu" subtitle="Remarketing vs Prospecting kampanyalar">
            {!monthly.unifiedCampaignData ? (
              <p className="text-sm text-gray-400">Kampanya verisi bulunamadı.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className={TH}>Segment</th>
                        <th className={THR}>Kampanya Sayısı</th>
                        <th className={THR}>Harcama</th>
                        <th className={THC}>Harcama Payı</th>
                        <th className={THR}>Gelir</th>
                        <th className={THC}>Gelir Payı</th>
                        <th className={THC}>ROAS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className={TDB}>Remarketing</td>
                        <td className={TDR}>{remarketingAnalysis.remarketing.count}</td>
                        <td className={TDR}>{fmtCur(remarketingAnalysis.remarketing.cost)}</td>
                        <td className={TDC}>{fmtPct(remarketingAnalysis.remarketing.costShare)}</td>
                        <td className={TDR}>{fmtCur(remarketingAnalysis.remarketing.revenue)}</td>
                        <td className={TDC}>{fmtPct(remarketingAnalysis.remarketing.revenueShare)}</td>
                        <td className={TDC}>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(remarketingAnalysis.remarketing.roas)}`}>
                            {fmtRoas(remarketingAnalysis.remarketing.roas)}x
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className={TDB}>Prospecting</td>
                        <td className={TDR}>{remarketingAnalysis.prospecting.count}</td>
                        <td className={TDR}>{fmtCur(remarketingAnalysis.prospecting.cost)}</td>
                        <td className={TDC}>{fmtPct(remarketingAnalysis.prospecting.costShare)}</td>
                        <td className={TDR}>{fmtCur(remarketingAnalysis.prospecting.revenue)}</td>
                        <td className={TDC}>{fmtPct(remarketingAnalysis.prospecting.revenueShare)}</td>
                        <td className={TDC}>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${roasBg(remarketingAnalysis.prospecting.roas)}`}>
                            {fmtRoas(remarketingAnalysis.prospecting.roas)}x
                          </span>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-900">
                        <td className={TDB}>Toplam</td>
                        <td className={TDBR}>{remarketingAnalysis.remarketing.count + remarketingAnalysis.prospecting.count}</td>
                        <td className={TDBR}>{fmtCur(remarketingAnalysis.remarketing.cost + remarketingAnalysis.prospecting.cost)}</td>
                        <td className={TDC}>100%</td>
                        <td className={TDBR}>{fmtCur(remarketingAnalysis.remarketing.revenue + remarketingAnalysis.prospecting.revenue)}</td>
                        <td className={TDC}>100%</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Visual comparison bar */}
                <div className="mt-6 space-y-3">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Harcama Dağılımı</p>
                    <div className="h-6 flex rounded overflow-hidden">
                      <div className="bg-gray-900 h-full" style={{ width: `${remarketingAnalysis.remarketing.costShare}%` }} />
                      <div className="bg-gray-300 h-full" style={{ width: `${remarketingAnalysis.prospecting.costShare}%` }} />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                      <span>Remarketing {fmtPct(remarketingAnalysis.remarketing.costShare)}</span>
                      <span>Prospecting {fmtPct(remarketingAnalysis.prospecting.costShare)}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Gelir Dağılımı</p>
                    <div className="h-6 flex rounded overflow-hidden">
                      <div className="bg-gray-900 h-full" style={{ width: `${remarketingAnalysis.remarketing.revenueShare}%` }} />
                      <div className="bg-gray-300 h-full" style={{ width: `${remarketingAnalysis.prospecting.revenueShare}%` }} />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                      <span>Remarketing {fmtPct(remarketingAnalysis.remarketing.revenueShare)}</span>
                      <span>Prospecting {fmtPct(remarketingAnalysis.prospecting.revenueShare)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 9 — Ürün Bazlı Performans ══════════════════ */}
          <Section title="9. Ürün Bazlı Performans" subtitle="Tüm dönem — Gelire göre sıralı Top 20">
            {extraLoading ? <LoadingSpinner /> : topItems.length === 0 ? (
              <p className="text-sm text-gray-400">Ürün verisi bulunamadı.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-900">
                      <th className={TH}>#</th>
                      <th className={TH}>Ürün Adı</th>
                      <th className={THR}>Satış Adedi</th>
                      <th className={THR}>Gelir</th>
                      <th className={THR}>Ort. Fiyat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {topItems.map((item, i) => {
                      const rev = item.revenue || item.item_revenue || 0;
                      const qty = item.quantity || item.units_sold || item.items_sold || 0;
                      const avgPrice = qty > 0 ? rev / qty : 0;
                      return (
                        <tr key={i}>
                          <td className={TD}>{i + 1}</td>
                          <td className={`${TD} max-w-[300px] truncate`} title={item.item_name || item.product_name || item.name}>
                            {item.item_name || item.product_name || item.name || '—'}
                          </td>
                          <td className={TDR}>{fmtNum(qty)}</td>
                          <td className={TDBR}>{fmtCur(rev)}</td>
                          <td className={TDR}>{fmtCur(avgPrice)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 10 — Performance vs Branding ══════════════════ */}
          <Section title="10. Bütçe Dağılım Raporu" subtitle="Performance vs Branding — Son 6 ay">
            {budgetDistribution.length === 0 ? (
              <p className="text-sm text-gray-400">Kategori verisi bulunamadı.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className={TH}>Ay</th>
                        <th className={THR}>Performance</th>
                        <th className={THR}>Branding</th>
                        <th className={THC}>Performance %</th>
                        <th className={THC}>Branding %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {budgetDistribution.map((r, i) => (
                        <tr key={i}>
                          <td className={TDB}>{r.month}</td>
                          <td className={TDR}>{fmtCur(r.perfCost)}</td>
                          <td className={TDR}>{fmtCur(r.brandCost)}</td>
                          <td className={TDC}>
                            <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">
                              {fmtPct(r.perfPct)}
                            </span>
                          </td>
                          <td className={TDC}>
                            <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-800">
                              {fmtPct(r.brandPct)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-900">
                        <td className={TDB}>Toplam</td>
                        <td className={TDBR}>{fmtCur(budgetDistribution.reduce((s, r) => s + r.perfCost, 0))}</td>
                        <td className={TDBR}>{fmtCur(budgetDistribution.reduce((s, r) => s + r.brandCost, 0))}</td>
                        <td className={TDC}>
                          <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">
                            {fmtPct(
                              budgetDistribution.reduce((s, r) => s + r.perfCost, 0) /
                              (budgetDistribution.reduce((s, r) => s + r.total, 0) || 1) * 100
                            )}
                          </span>
                        </td>
                        <td className={TDC}>
                          <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-800">
                            {fmtPct(
                              budgetDistribution.reduce((s, r) => s + r.brandCost, 0) /
                              (budgetDistribution.reduce((s, r) => s + r.total, 0) || 1) * 100
                            )}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Visual bar */}
                <div className="mt-6">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Toplam Dağılım</p>
                  {(() => {
                    const totalPerf = budgetDistribution.reduce((s, r) => s + r.perfCost, 0);
                    const totalBrand = budgetDistribution.reduce((s, r) => s + r.brandCost, 0);
                    const total = totalPerf + totalBrand;
                    const perfShare = total > 0 ? (totalPerf / total * 100) : 0;
                    return (
                      <>
                        <div className="h-8 flex rounded overflow-hidden">
                          <div className="bg-gray-900 h-full flex items-center justify-center text-[10px] font-bold text-white" style={{ width: `${perfShare}%` }}>
                            {perfShare > 15 ? `Performance ${perfShare.toFixed(0)}%` : ''}
                          </div>
                          <div className="bg-gray-200 h-full flex items-center justify-center text-[10px] font-bold text-gray-700" style={{ width: `${100 - perfShare}%` }}>
                            {(100 - perfShare) > 15 ? `Branding ${(100 - perfShare).toFixed(0)}%` : ''}
                          </div>
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-gray-500">
                          <span>Performance: {fmtCur(totalPerf)}</span>
                          <span>Branding: {fmtCur(totalBrand)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            )}
          </Section>

          {/* ══════════════════ BÖLÜM 11 — Search Keyword Performans Özeti ══════════════════ */}
          <Section title="11. Search Keyword Performans Özeti" subtitle="Google Ads — Ürün Kategorisi Bazlı Harcama & Gelir">
            {(() => {
              const kwData = [
                { category: 'iPhone 13 (Tümü)', cost: 118213.63, revenue: 842056.80 },
                { category: 'iPhone 14 (Tümü)', cost: 80953.96, revenue: 110610 },
                { category: 'iPhone 15 (Tümü)', cost: 31940.97, revenue: 91881 },
                { category: 'MacBook (Tümü)', cost: 28780.69, revenue: 474242.76 },
                { category: 'Brand (Yenilio)', cost: 268554.18, revenue: 11426551.12 },
                { category: 'Mağazalar Çevresi', cost: 2337.30, revenue: 10631 },
              ];
              const totCost = kwData.reduce((s, r) => s + r.cost, 0);
              const totRev = kwData.reduce((s, r) => s + r.revenue, 0);
              const channelData = [
                { channel: 'Search', cost: 719378.40, revenue: 13794546.59 },
                { channel: 'Performance Max', cost: 472049.05, revenue: 5802170.75 },
                { channel: 'Display', cost: 42718.11, revenue: 0 },
                { channel: 'Video', cost: 17182.08, revenue: 0 },
              ];
              const chTotCost = channelData.reduce((s, r) => s + r.cost, 0);
              const chTotRev = channelData.reduce((s, r) => s + r.revenue, 0);
              return (
                <>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Ürün Kategorisi Bazlı (Search)</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-900">
                          <th className={TH}>Kategori</th>
                          <th className={THR}>Harcama</th>
                          <th className={THR}>Gelir</th>
                          <th className={THR}>ROAS</th>
                          <th className={THC}>Harcama Payı</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {kwData.map((r, i) => (
                          <tr key={i}>
                            <td className={TDB}>{r.category}</td>
                            <td className={TDR}>{fmtCur(r.cost)}</td>
                            <td className={TDBR}>{fmtCur(r.revenue)}</td>
                            <td className={TDR}>{r.cost > 0 ? (r.revenue / r.cost).toFixed(2) + 'x' : '—'}</td>
                            <td className={TDC}>
                              <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-100 text-gray-700">
                                {fmtPct(totCost > 0 ? (r.cost / totCost * 100) : 0)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-900">
                          <td className={TDB}>Toplam</td>
                          <td className={TDBR}>{fmtCur(totCost)}</td>
                          <td className={TDBR}>{fmtCur(totRev)}</td>
                          <td className={TDR}>{totCost > 0 ? (totRev / totCost).toFixed(2) + 'x' : '—'}</td>
                          <td className={TDC}>
                            <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">100%</span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="mt-8">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Kanal Bazlı Özet</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-gray-900">
                            <th className={TH}>Kanal</th>
                            <th className={THR}>Harcama</th>
                            <th className={THR}>Gelir</th>
                            <th className={THR}>ROAS</th>
                            <th className={THC}>Harcama Payı</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {channelData.map((r, i) => (
                            <tr key={i}>
                              <td className={TDB}>{r.channel}</td>
                              <td className={TDR}>{fmtCur(r.cost)}</td>
                              <td className={TDBR}>{fmtCur(r.revenue)}</td>
                              <td className={TDR}>{r.cost > 0 ? (r.revenue / r.cost).toFixed(2) + 'x' : '—'}</td>
                              <td className={TDC}>
                                <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-100 text-gray-700">
                                  {fmtPct(chTotCost > 0 ? (r.cost / chTotCost * 100) : 0)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-900">
                            <td className={TDB}>Toplam</td>
                            <td className={TDBR}>{fmtCur(chTotCost)}</td>
                            <td className={TDBR}>{fmtCur(chTotRev)}</td>
                            <td className={TDR}>{chTotCost > 0 ? (chTotRev / chTotCost).toFixed(2) + 'x' : '—'}</td>
                            <td className={TDC}>
                              <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">100%</span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}
          </Section>

          {/* ══════════════════ BÖLÜM 12 — Müşteri Memnuniyeti & Söylem Çalışması ══════════════════ */}
          <Section title="12. Müşteri Memnuniyeti & Söylem Stratejisi" subtitle="Anket Sonuçları ve İletişim Önerileri">
            {(() => {
              const satisfactionRate = 97;
              const referralRate = 80;
              const npsEstimate = Math.round((referralRate / 100) * 10 - 1);
              return (
                <>
                  {/* KPI Kartları */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-green-700">%{satisfactionRate}</div>
                      <div className="text-[10px] font-bold text-green-600 uppercase tracking-wider mt-1">Memnuniyet Oranı</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-blue-700">%{referralRate}</div>
                      <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mt-1">Tavsiye Oranı</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-purple-700">{npsEstimate}/10</div>
                      <div className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mt-1">Tahmini NPS Skoru</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-amber-700">Güçlü</div>
                      <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mt-1">Marka Sadakati</div>
                    </div>
                  </div>

                  {/* Söylem Stratejisi Tablosu */}
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Kanal Bazlı Söylem Önerileri</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-900">
                          <th className={TH}>Kanal</th>
                          <th className={TH}>Hedef Kitle</th>
                          <th className={TH}>Ana Söylem</th>
                          <th className={TH}>Destekleyici Mesaj</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className={TDB}>Google Search (Brand)</td>
                          <td className={TD}>Markayı bilen kullanıcı</td>
                          <td className={TD}>"Yenilio ile güvenle al — %97 müşteri memnuniyeti"</td>
                          <td className={TD}>12 aya varan garanti, ücretsiz kargo, koşulsuz iade</td>
                        </tr>
                        <tr>
                          <td className={TDB}>Google Search (Generic)</td>
                          <td className={TD}>İkinci el/yenilenmiş arayan</td>
                          <td className={TD}>"İkinci el arayanlar için daha güvenli adres: Yenilio"</td>
                          <td className={TD}>Sıfır gibi, yarı fiyatına — her 10 müşterimizden 8'i bizi arkadaşına öneriyor</td>
                        </tr>
                        <tr>
                          <td className={TDB}>Google Search (Rakip)</td>
                          <td className={TD}>Sahibinden/Letgo arayanlar</td>
                          <td className={TD}>"İkinci elden daha güvenlisi: Yenilio"</td>
                          <td className={TD}>Profesyonel test, teknik kontrol, gerçek garanti — bireysel satıcı riski yok</td>
                        </tr>
                        <tr>
                          <td className={TDB}>Performance Max</td>
                          <td className={TD}>Geniş kitle, retargeting</td>
                          <td className={TD}>"Apple dünyasına uygun fiyatla gir — Yenilio güvencesiyle"</td>
                          <td className={TD}>21 gün deneme, ücretsiz kargo</td>
                        </tr>
                        <tr>
                          <td className={TDB}>Meta Ads (Prospecting)</td>
                          <td className={TD}>Apple ilgi alanı, teknoloji meraklısı</td>
                          <td className={TD}>"Yenilenmiş demek, eski demek değil — %97 müşteri memnuniyeti"</td>
                          <td className={TD}>Video: unboxing + müşteri yorumları</td>
                        </tr>
                        <tr>
                          <td className={TDB}>Meta Ads (Remarketing)</td>
                          <td className={TD}>Siteyi ziyaret eden</td>
                          <td className={TD}>"Hâlâ karar veremedim mi? 8/10 kişi bizi tavsiye ediyor"</td>
                          <td className={TD}>Son 3 gün kalan stok, sepete eklenen ürün hatırlatma</td>
                        </tr>
                        <tr>
                          <td className={TDB}>E-posta / SMS</td>
                          <td className={TD}>Mevcut müşteriler</td>
                          <td className={TD}>"Yeni model geldi — eski cihazını getir, yenisine geç"</td>
                          <td className={TD}>Upgrade teklifi, eski cihaz takası</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Anahtar Mesajlar */}
                  <div className="mt-8">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Tüm Kanallarda Kullanılacak Anahtar Mesajlar</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="border rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-gray-900 uppercase">Güven & Kalite</h4>
                        <ul className="text-sm text-gray-700 space-y-1.5">
                          <li>• "%97 müşteri memnuniyeti — binlerce mutlu müşteri"</li>
                          <li>• "54 adımlık teknik kontrol sürecinden geçirilmiş"</li>
                          <li>• "12 Ay TSE Garantisi + 21 Gün İade + 90 Gün Değişim"</li>
                          <li>• "Koşulsuz iade hakkı — risk sizde değil"</li>
                        </ul>
                      </div>
                      <div className="border rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-gray-900 uppercase">Sosyal Kanıt</h4>
                        <ul className="text-sm text-gray-700 space-y-1.5">
                          <li>• "Her 10 müşterimizden 8'i bizi bir arkadaşına öneriyor"</li>
                          <li>• "Binlerce gerçek müşteri yorumu"</li>
                          <li>• "Türkiye'nin en güvenilir yenilenmiş teknoloji platformu"</li>
                          <li>• "#YenilioGüvencesi ile yüz binlerce cihaz teslim edildi"</li>
                        </ul>
                      </div>
                      <div className="border rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-gray-900 uppercase">Fiyat & Değer</h4>
                        <ul className="text-sm text-gray-700 space-y-1.5">
                          <li>• "Sıfırının yarı fiyatına, sıfır gibi performans"</li>
                          <li>• "Taksitle yenilenmiş iPhone — aylık sadece ₺X'den başlayan"</li>
                          <li>• "Akıllı tercih: Yenilenmiş al, bütçeni koru"</li>
                          <li>• "Eski cihazını getir, yenisini al — takas fırsatı"</li>
                        </ul>
                      </div>
                      <div className="border rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-gray-900 uppercase">Aciliyet & CTA</h4>
                        <ul className="text-sm text-gray-700 space-y-1.5">
                          <li>• "Sınırlı stok — bu fiyata son X adet"</li>
                          <li>• "Bugün al, yarın kapında"</li>
                          <li>• "21 gün dene, beğenmezsen iade et"</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                </>
              );
            })()}
          </Section>

          {/* ══════════════════ BÖLÜM 13 — Toplantı Çıktıları & Yapılacaklar ══════════════════ */}
          <Section title="13. Yenilio & Gürgençler — Strateji Toplantısı Yapılacaklar" subtitle="Dijital İletişim Yol Haritası">
            <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-800 leading-relaxed">

              {/* SOL KOLON */}
              <div className="space-y-4">
                <p className="font-bold text-base">Söylem & Marka Konumlandırma Playbook'u</p>
                <ul className="ml-4 space-y-1">
                  <li>- Reklamlarda marka konumlandırması doğru anlatılmıyor — güven mesajları zayıf</li>
                  <li>- 10 ana değer mesajı belirlendi, playbook hazırlanacak (hangi mesaj hangi kampanyada):</li>
                  <li className="ml-4">· Apple Yetkili Yenileme Partneri</li>
                  <li className="ml-4">· Gürgençler Deneyimi & Güvencesi</li>
                  <li className="ml-4">· 15 Yıllık AppleCare Tecrübesi</li>
                  <li className="ml-4">· Sadece Apple Ürünleri — Android satmıyoruz</li>
                  <li className="ml-4">· 21 Gün İade & 90 Gün Değişim Garantisi</li>
                  <li className="ml-4">· 12 Ay TSE Garantisi</li>
                  <li className="ml-4">· Ticaret Bakanlığı Lisanslı Yenileme Merkezi</li>
                  <li className="ml-4">· Bilişim Vadisi Kampüsü Üretimi</li>
                  <li className="ml-4">· 4 Büyük Şehirde Fiziksel Mağaza (İstanbul, Ankara, Bursa, İzmir)</li>
                  <li className="ml-4">· Mac · iPad · Watch Butik Uzmanı</li>
                </ul>

                <p className="font-bold text-base pt-2">Hedef Kitle Segmentasyonu</p>
                <ul className="ml-4 space-y-1">
                  <li>- Mevcut kampanyalar çok geniş kitleye gidiyor, segmentlere ayrılacak</li>
                  <li>- Segment 1: iPhone Upgrade kitle — eski model kullanıcısı, yenisine geçmek istiyor</li>
                  <li>- Segment 2: Mac kullanıcıları — çok değerli, yenilenmiş MacBook'ta rakip yok</li>
                  <li>- Segment 3: Üniversite öğrencileri — iPad + uygun fiyat + taksit</li>
                  <li>- Segment 4: Apple ekosistem kullanıcısı — cross-sell fırsatı</li>
                  <li>- Segment 5: Fiyat hassasiyeti yüksek — ucuz + güvenilir, 12 taksit önemli</li>
                </ul>

                <p className="font-bold text-base pt-2">Ürün Bazlı Reklam Stratejisi</p>
                <ul className="ml-4 space-y-1">
                  <li>- Hangi cihaz için hangi strateji kullanılıyor kontrol edilecek</li>
                  <li>- MacBook: Yenilenmiş satan rakip yok — marka tanıtım kapısı, bütçe artırılacak</li>
                  <li className="ml-4">· 400 adet Intel MacBook stok — özel kurgular kurulacak</li>
                  <li>- iPad: Öğrenci hedefleme, üniversite dönemi kampanyaları</li>
                  <li>- Apple Watch: Aksesuar + ekosistem cross-sell</li>
                  <li>- iPhone 15/14: Performance odaklı ROAS kampanyaları</li>
                  <li>- iPhone 11: 2.000 adet stok — öğrenci, aile segmenti, ucuz + kolay + 12 taksit</li>
                  <li>- Stokta olan ürünlere özel kampanya açılacak, stok-kampanya eşleştirmesi yapılacak</li>
                </ul>

                <p className="font-bold text-base pt-2">Remarketing / Retargeting Güçlendirme</p>
                <ul className="ml-4 space-y-1">
                  <li>- Mevcut retargeting zayıf — bütçenin ~%30'u bu tarafa ayrılacak</li>
                  <li>- 4 katmanlı yapı kurulacak:</li>
                  <li className="ml-4">· Katman 1: Site ziyaretçisi</li>
                  <li className="ml-4">· Katman 2: Ürün sayfası görüntüleyen</li>
                  <li className="ml-4">· Katman 3: Sepete ekleyen</li>
                  <li className="ml-4">· Katman 4: Eski müşteri (upgrade)</li>
                  <li>- Remarketing harcama, gelir ve ROAS raporu çıkarılacak</li>
                </ul>

                <p className="font-bold text-base pt-2">Söylem Bazlı A/B Test Çalışması</p>
                <ul className="ml-4 space-y-1">
                  <li>- Aynı ürün için farklı söylemlerle kreatifler test edilecek</li>
                  <li className="ml-4">· Kreatif A: iPhone + fiyat + 12 taksit</li>
                  <li className="ml-4">· Kreatif B: 21 gün iade hakkı + güven mesajı</li>
                  <li className="ml-4">· Kreatif C: Apple Yetkili Yenileme Partneri + cihaz görseli</li>
                  <li className="ml-4">· Kreatif D: Gürgençler güvencesi + 15 yıl deneyim</li>
                  <li>- Veri ile konuşmamız gerekiyor — söylemlerin satışa etkisi ölçülecek</li>
                </ul>

                <p className="font-bold text-base pt-2">SEO & Organik Sayfa Çalışması</p>
                <ul className="ml-4 space-y-1">
                  <li>- Organik sayfa başlıkları güncellenecek</li>
                  <li>- Kategori bazlı SEO odaklı sayfa çalışması yapılacak</li>
                  <li>- 10 söylem tüm sayfalara yansıtılacak</li>
                  <li>- SEO içerik girme teklifi verilecek</li>
                </ul>
              </div>

              {/* SAG KOLON */}
              <div className="space-y-4">
                <p className="font-bold text-base">Kreatif Performans Analizi</p>
                <ul className="ml-4 space-y-1">
                  <li>- Son 6 ayda en çok satış getiren kreatifler belirlenecek</li>
                  <li>- MacBook tarafında ve iPhone 11 tarafında ayrı ayrı</li>
                  <li>- Reklam kreatifleri çok benzer — farklılaştırılacak</li>
                  <li>- Sadece katalog reklamı dönüyor, marka/güven kreatifleri eksik</li>
                  <li>- Hedef bütçe mix: Catalog %50, Conversion %30, Marka/Güven %20</li>
                </ul>

                <p className="font-bold text-base pt-2">Satış Dönüşüm & Funnel Analizi</p>
                <ul className="ml-4 space-y-1">
                  <li>- Reklamdan gelen kullanıcı hangi adımda kayboluyor — funnel raporu</li>
                  <li>- Hangi reklam satış getiriyor — kampanya bazlı trafik-satış eşleştirmesi</li>
                  <li>- "Bugün reklamı kessek satış kaç düşer?" sorusunun cevabı hazırlanacak</li>
                  <li>- Satışı artıracak içgörü — sadece rapor değil aksiyon önerisi</li>
                </ul>

                <p className="font-bold text-base pt-2">Rakip Analizi</p>
                <ul className="ml-4 space-y-1">
                  <li>- Getmobil, EasyCep, HB İletişim incelenecek</li>
                  <li>- Hangi söylemi kullanıyorlar, nereye gidiyorlar, nerede iş yapıyorlar</li>
                  <li>- Trendyol vs Yenilio karşılaştırması</li>
                </ul>

                <p className="font-bold text-base pt-2">Bütçe & ROAS Raporlaması</p>
                <ul className="ml-4 space-y-1">
                  <li>- Kanal bazlı bütçe-satış raporu: Google, Meta, YouTube — hangisi para kazandırıyor</li>
                  <li>- ROAS raporu: kanal bazlı 3x, 6x hedefleri</li>
                  <li>- 90 günlük büyüme planı — 3 aylık trafik, satış, ROAS hedefleri</li>
                </ul>

                <p className="font-bold text-base pt-2">CRM & Müşteri Datası</p>
                <ul className="ml-4 space-y-1">
                  <li>- Mevcut müşteri datası reklam platformlarına yüklenecek (hash'lenmiş)</li>
                  <li>- Cross-sell / upsell / buyback kampanyaları kurulacak</li>
                  <li>- Trendyol yorumlarını siteye çekme konusu değerlendirilecek</li>
                </ul>

                <p className="font-bold text-base pt-2">Raporlama Yapısı</p>
                <ul className="ml-4 space-y-1">
                  <li>- Ali Bey'e rapor gönderilecek:</li>
                  <li className="ml-4">· En çok satış getiren ürün ve anahtar kelime</li>
                  <li className="ml-4">· En iyi performans gösteren kreatif</li>
                  <li className="ml-4">· Kanal bazlı satış grafiği</li>
                  <li className="ml-4">· Retargeting satış oranı</li>
                  <li className="ml-4">· Mağaza trafik etkisi</li>
                  <li className="ml-4">· Aksesuar gelişme durumu</li>
                </ul>

                <div className="mt-4 border-t pt-4">
                  <p className="font-bold text-base">5 Büyük Büyüme Fırsatı</p>
                  <ul className="ml-4 space-y-1 mt-2">
                    <li>- Apple + Gürgençler güvencesi reklamlarda görünür kılınacak</li>
                    <li>- MacBook & iPad — rakipsiz alan, bütçe artırılacak</li>
                    <li>- SEO & organik — doğal aramada 1. sıra, özel landing page'ler</li>
                    <li>- Güven paketi — 21 gün iade, 90 gün değişim, fiziksel mağaza her reklamda</li>
                    <li>- Stok bazlı kurgular — iPhone 11 (2K stok) + Intel MacBook (400 stok)</li>
                  </ul>
                </div>
              </div>

            </div>
          </Section>

          {/* ══════════════════ BÖLÜM 14 — Blog İçerik Planı ══════════════════ */}
          <BlogContentPlan />
        </>
      )}
    </div>
  );
}
